'use client';
import React, { useContext, useState } from 'react';
import PostFiltersComp from './postFilters/postFilters.index';
import styles from './posts.module.scss';
import { __PostsFiltersType, __PostsPropsType } from './posts.type';
import { RestAPI, useBP, useRealTimeState, useThen } from 'scripts';
import { Button, Drawer, Dropdown, Empty, message, Spin } from 'antd';
import { PostCardComp } from 'components';
import { __GetParentCategories, __PostFetchURL, __PostsPageURL } from './posts.script';
import { GlobalContext } from 'context';
import { API } from 'data';
import { CategoryFieldType, CategoryType, PaginationType, PostType } from 'types';
import { useSearchParams } from 'next/navigation';
import { InView } from 'react-intersection-observer';
import { useQuery, useQueryClient } from '@tanstack/react-query';

function PostsComp(props: __PostsPropsType) {
    const query = useQuery({ queryKey: ['posts', 'list'], initialData: props.initialData });
    const [filterModal, setFilterModal] = useState(false);
    const [filters, setFilters] = useState<__PostsFiltersType>({ ...props.initialFilter, page: 1 });
    const [filterData, setFilterData] = useState<typeof props.initialFilterData | undefined>(props.initialFilterData);
    const bp = useBP();
    const isDesktop = bp?.LG_GTE;
    const { locations, categories } = useContext(GlobalContext);
    const searchParams = useSearchParams();
    const loadMore = useRealTimeState(false);
    const queryClient = useQueryClient();

    const posts = query.data;

    // update posts
    useThen(() => {
        // change path shallow
        const path = __PostsPageURL(filters);
        window.history.replaceState(null, '', path);

        // get data
        if (filters.page === 1) queryClient.setQueryData(['posts', 'list'], undefined);
        else loadMore.set(true);
        RestAPI.get<PaginationType<PostType<'list'>>>(__PostFetchURL(filters, locations))
            .then((res) => {
                const data =
                    filters.page === 1 ? res.data : { ...res.data, data: [...(posts?.data ?? []), ...res.data.data] };
                queryClient.setQueryData(['posts', 'list'], data);
            })
            .catch(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
                message.error('خطایی در بارگذاری آگهی های بیشتر رخ داده است. لطفا مجددا تلاش نمایید');
            })
            .then(() => {
                loadMore.set(false);
            });
    }, [filters, locations]);

    // update filter data
    useThen(() => {
        // check if should update filter data
        if (filterData && filters.category?.slug !== filterData.category?.slug) {
            // if it back to no-parent state
            if (!filters.category) setFilterData({ subcategories: __GetParentCategories(categories) });
            else {
                // get data
                setFilterData(undefined);
                Promise.all([
                    RestAPI.get<CategoryType<'detail'>>(API.category.detail(filters.category.slug)),
                    RestAPI.get<PaginationType<CategoryType<'list'>>>(API.category.list, {
                        parent: filters.category.id,
                        page_size: 100,
                    }),
                    RestAPI.get<PaginationType<CategoryFieldType<'detail'>>>(API.categoryField.list, {
                        is_filter: true,
                        page_size: 100,
                        category_ancestors: filters.category.id,
                    }),
                ]).then((res) =>
                    setFilterData({
                        category: res[0].data,
                        subcategories: res[1].data.data,
                        variantFields: res[2].data.data,
                    }),
                );
            }
        }
    }, [filters.category]);

    useThen(() => {
        const search = searchParams.get('search');
        if (search?.length && filters.search !== search) setFilters({ ...filters, page: 1, search });
        else if ((!search || search.length === 0) && filters.search)
            setFilters({ ...filters, page: 1, search: undefined });
    }, [searchParams]);

    console.log(posts, filters);

    return (
        <div className={`${styles['post-list-layout']} d-flex`}>
            {!bp || isDesktop ? (
                <div className={`${styles['filter-layout']}`}>
                    <PostFiltersComp filters={filters} setFilters={setFilters} filterData={filterData} />
                </div>
            ) : (
                <Drawer open={filterModal} onClose={() => setFilterModal(false)} width={'100%'}>
                    <PostFiltersComp
                        filters={filters}
                        setFilters={setFilters}
                        filterData={filterData}
                        onClose={() => setFilterModal(false)}
                    />
                </Drawer>
            )}
            <div className={`${styles['post-layout']} flex-column`}>
                <div className="d-md-none g-1 mv-2">
                    <Button className="flex-1" onClick={() => setFilterModal(true)}>
                        فیلترها
                    </Button>
                    <Dropdown menu={{ items: [{ label: 'جدیدترین', key: '-renewed_at' }] }} trigger={['click']}>
                        <Button className="flex-1" type="primary">
                            مرتب سازی
                        </Button>
                    </Dropdown>
                </div>
                {posts ? (
                    posts?.data.length ? (
                        <>
                            <div className={`${styles['grid']}`}>
                                {posts.data.map((post, index) => (
                                    <React.Fragment key={index}>
                                        {posts.num_of_pages !== filters.page && index >= posts.data.length - 12 ? (
                                            <InView
                                                as="div"
                                                onChange={(inView) => {
                                                    if (inView && !loadMore.get())
                                                        setFilters({ ...filters, page: (filters.page ?? 1) + 1 });
                                                }}
                                            >
                                                <PostCardComp data={post} />
                                            </InView>
                                        ) : (
                                            <PostCardComp data={post} />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                            {posts.num_of_pages !== filters.page ? (
                                <InView
                                    as="div"
                                    className="center-content pv-2 w-100"
                                    onChange={(inView) => {
                                        if (inView && !loadMore.get())
                                            setFilters({ ...filters, page: (filters.page ?? 1) + 1 });
                                    }}
                                >
                                    <Spin />
                                </InView>
                            ) : null}
                        </>
                    ) : (
                        <Empty
                            className="pv-6 flex-1 center-content flex-column"
                            description={
                                'آگهی ای یافت نشد. ' + locations
                                    ? 'برای جستجوی گسترده‌تر فیلترهای مورد نظر خود را تغییر دهید.'
                                    : ''
                            }
                        />
                    )
                ) : (
                    <div className={`w-100 pv-9 center-content flex-1`}>
                        <Spin />
                    </div>
                )}
            </div>
        </div>
    );
}

export default PostsComp;
